// Встановлюємо базовий URL API
// Якщо REACT_APP_API_URL не встановлений, визначаємо автоматично
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // Якщо ми на localhost - використовуємо локальний бекенд
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8000';
  }
  // Інакше використовуємо production URL
  return 'https://ratex.top';
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Отримати список бірж
  async getExchanges() {
    return this.request('/api/exchanges');
  }

  // Отримати торгові пари з Binance
  async getBinanceTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    
    return this.request(`/api/binance/trading-pairs?${params}`);
  }

  // Отримати торгові пари з OKX
  async getOKXTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    
    return this.request(`/api/okx/trading-pairs?${params}`);
  }

  // Отримати торгові пари з KuCoin
  async getKuCoinTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    
    return this.request(`/api/kucoin/trading-pairs?${params}`);
  }

  // Отримати торгові пари з Gate
  async getGateTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/gate/trading-pairs?${params}`);
  }

  // Отримати торгові пари з MEXC
  async getMEXCTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/mexc/trading-pairs?${params}`);
  }

  // Отримати торгові пари з HTX
  async getHTXTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/htx/trading-pairs?${params}`);
  }

  // Отримати торгові пари з Bybit
  async getBybitTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/bybit/trading-pairs?${params}`);
  }

  // Отримати торгові пари з Bitget
  async getBitgetTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/bitget/trading-pairs?${params}`);
  }

  // Отримати ставку фінансування з Binance
  async getBinanceFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    // Формуємо UTC межі обраних календарних дат, щоб уникнути зсувів часових поясів
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    
    return this.request(`/api/binance/funding-rate/${symbol}?${params}`);
  }

  // Отримати ставку фінансування з OKX
  async getOKXFundingRate(symbol, startDate, endDate) {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
    });
    
    return this.request(`/api/okx/funding-rate/${symbol}?${params}`);
  }

  // Отримати ставку фінансування з KuCoin
  async getKuCoinFundingRate(symbol, startDate, endDate) {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
    });
    
    return this.request(`/api/kucoin/funding-rate/${symbol}?${params}`);
  }

  // Отримати ставку фінансування з Gate
  async getGateFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/gate/funding-rate/${symbol}?${params}`);
  }

  // Отримати ставку фінансування з MEXC
  async getMEXCFundingRate(symbol, startDate, endDate) {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
    });
    return this.request(`/api/mexc/funding-rate/${symbol}?${params}`);
  }

  // Отримати ставку фінансування з HTX
  async getHTXFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/htx/funding-rate/${symbol}?${params}`);
  }

  // Отримати ставку фінансування з Bybit
  async getBybitFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/bybit/funding-rate/${symbol}?${params}`);
  }

  // Отримати ставку фінансування з Bitget (історія)
  async getBitgetFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/bitget/funding-rate/${symbol}?${params}`);
  }

  // Отримати торгові пари з Aster
  async getAsterTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/aster/trading-pairs?${params}`);
  }

  // Отримати ставку фінансування з Aster (історія)
  async getAsterFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/aster/funding-rate/${symbol}?${params}`);
  }

  // Отримати торгові пари з Hyperliquid
  async getHyperliquidTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/hyperliquid/trading-pairs?${params}`);
  }

  // Отримати ставку фінансування з Hyperliquid (історія)
  async getHyperliquidFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/hyperliquid/funding-rate/${symbol}?${params}`);
  }

  // Отримати торгові пари з Bitfinex
  async getBitfinexTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/bitfinex/trading-pairs?${params}`);
  }

  // Отримати ставку фінансування з Bitfinex (історія)
  async getBitfinexFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/bitfinex/funding-rate/${symbol}?${params}`);
  }

  // ============= CRYPTO.COM ===============
  async getCryptocomTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/cryptocom/trading-pairs?${params}`);
  }

  async getCryptocomFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/cryptocom/funding-rate/${symbol}?${params}`);
  }

  // ============= COINEX ===============
  async getCoinexTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/coinex/trading-pairs?${params}`);
  }

  async getCoinexFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/coinex/funding-rate/${symbol}?${params}`);
  }

  // ============= POLONIEX ===============
  async getPoloniexTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/poloniex/trading-pairs?${params}`);
  }

  async getPoloniexFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/poloniex/funding-rate/${symbol}?${params}`);
  }

  // ============= XT.COM ===============
  async getXtcomTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/xtcom/trading-pairs?${params}`);
  }

  async getXtcomFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/xtcom/funding-rate/${symbol}?${params}`);
  }

  // ============= BITMART ===============
  async getBitmartTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/bitmart/trading-pairs?${params}`);
  }

  async getBitmartFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/bitmart/funding-rate/${symbol}?${params}`);
  }

  // ============= BINGX ===============
  async getBingxTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/bingx/trading-pairs?${params}`);
  }

  async getBingxFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/bingx/funding-rate/${symbol}?${params}`);
  }

  // ============= BITHUMB ===============
  async getBithumbTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/bithumb/trading-pairs?${params}`);
  }

  async getBithumbFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/bithumb/funding-rate/${symbol}?${params}`);
  }

  // ============= UPBIT ===============
  async getUpbitTradingPairs(search = '', page = 1, limit = 50, contractType = 'usdt-m') {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      contract_type: contractType,
    });
    return this.request(`/api/upbit/trading-pairs?${params}`);
  }

  async getUpbitFundingRate(symbol, startDate, endDate, contractType = 'usdt-m') {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const startOfDay = new Date(Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999));
    const params = new URLSearchParams({
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      contract_type: contractType,
    });
    return this.request(`/api/upbit/funding-rate/${symbol}?${params}`);
  }

  // Оновлення універсального методу
  async getFundingRate(exchangeId, symbol, startDate, endDate, contractType = null) {
    if (exchangeId === 'binance') {
      return this.getBinanceFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'okx') {
      return this.getOKXFundingRate(symbol, startDate, endDate);
    } else if (exchangeId === 'kucoin') {
      return this.getKuCoinFundingRate(symbol, startDate, endDate);
    } else if (exchangeId === 'gate') {
      return this.getGateFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'mexc') {
      return this.getMEXCFundingRate(symbol, startDate, endDate);
    } else if (exchangeId === 'htx') {
      return this.getHTXFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'bybit') {
      return this.getBybitFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'bitget') {
      return this.getBitgetFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'aster') {
      return this.getAsterFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'hyperliquid') {
      return this.getHyperliquidFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'bitfinex') {
      return this.getBitfinexFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'cryptocom') {
      return this.getCryptocomFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'coinex') {
      return this.getCoinexFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'poloniex') {
      return this.getPoloniexFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'xtcom') {
      return this.getXtcomFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'bitmart') {
      return this.getBitmartFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'bingx') {
      return this.getBingxFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'bithumb') {
      return this.getBithumbFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else if (exchangeId === 'upbit') {
      return this.getUpbitFundingRate(symbol, startDate, endDate, contractType || 'usdt-m');
    } else {
      throw new Error(`Непідтримувана біржа: ${exchangeId}`);
    }
  }

  // Універсальний метод для отримання торгових пар
  async getTradingPairs(exchangeId, search = '', page = 1, limit = 50, contractType = null) {
    if (exchangeId === 'binance') {
      return this.getBinanceTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'okx') {
      return this.getOKXTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'kucoin') {
      return this.getKuCoinTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'gate') {
      return this.getGateTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'mexc') {
      return this.getMEXCTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'htx') {
      return this.getHTXTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'bybit') {
      return this.getBybitTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'bitget') {
      return this.getBitgetTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'aster') {
      return this.getAsterTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'hyperliquid') {
      return this.getHyperliquidTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'bitfinex') {
      return this.getBitfinexTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'cryptocom') {
      return this.getCryptocomTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'coinex') {
      return this.getCoinexTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'poloniex') {
      return this.getPoloniexTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'xtcom') {
      return this.getXtcomTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'bitmart') {
      return this.getBitmartTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'bingx') {
      return this.getBingxTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'bithumb') {
      return this.getBithumbTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else if (exchangeId === 'upbit') {
      return this.getUpbitTradingPairs(search, page, limit, contractType || 'usdt-m');
    } else {
      throw new Error(`Непідтримувана біржа: ${exchangeId}`);
    }
  }

  // Отримати денні дані ставок фінансування
  async getDailyFundingData(currency = 'BTC', date = null) {
    const params = new URLSearchParams({ currency });
    if (date) {
      params.append('date', date);
    }
    return this.request(`/api/daily-funding-data?${params}`);
  }

  // Отримати доступні дати для валюти
  async getAvailableDates(currency = 'BTC') {
    return this.request(`/api/available-dates?currency=${currency}`);
  }

  // Отримати/задати час запуску планувальника (UTC)
  async getSchedulerTime() {
    return this.request('/api/scheduler/time');
  }

  async setSchedulerTime(hour, minute, password_hash = null) {
    const paramsObj = { hour: hour.toString(), minute: minute.toString() };
    if (password_hash) paramsObj.password_hash = password_hash;
    const params = new URLSearchParams(paramsObj);
    return this.request(`/api/scheduler/time?${params}`, {
      method: 'POST'
    });
  }

  // Зберегти денні дані (ручний запуск)
  async saveDailyData() {
    return this.request('/api/save-daily-data', {
      method: 'POST',
    });
  }

  // Отримати збережені денні дані
  async getSavedDailyData(date = null) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/api/saved-daily-data${params}`);
  }

  // Отримати статус планувальника
  async getSchedulerStatus() {
    return this.request('/api/scheduler/status');
  }

  // Health check
  async healthCheck() {
    return this.request('/health');
  }
}

const apiService = new ApiService();
export default apiService;
